'use client';
import React, { useState } from 'react';
import { TeamCardProps } from '@/components/card/types';
import clsx from 'clsx';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { Link } from '@/components/link/Link';
import Icon from '@/components/icon/Icon';
import { Overlay } from '@/components/overlay/Overlay';
import { renameDomProps } from '@/utils/sanitizeDomProps';
import { useTranslations } from '@/lib';

const TeamCard: React.FC<TeamCardProps> = React.memo(
  ({
    image,
    title,
    links,
    socialShare,
    phone,
    email,
    linkedIn,
    className,
    overlay,
    category,
    showBio,
    position,
    positionEn,
    address,
    addressEn,
    ...rest
  }) => {
    const hasImage = Boolean(image);
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const renamedProps = renameDomProps(rest);

    const { locale } = useTranslations();
    const displayPosition = locale === 'en' ? positionEn : position;
    const shouldShowBio = String(showBio) === 'true';

    return (
      <div
        className={clsx(
          'team-card flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-50 bg-white text-brand-700',
          className,
        )}
        {...renamedProps}
      >
        <div
          className={clsx(
            'aspect-w-16 aspect-h-9 max-h-96 w-full overflow-hidden bg-neutral-200',
            { 'bg-gray-100': !hasImage },
          )}
        >
          {hasImage ? (
            <BaseImage
              {...image}
              className="h-full w-full"
              classNameImage="w-full h-full object-cover"
              alt={title}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
            />
          ) : (
            <div className="flex items-center justify-center text-sm text-gray-400">
              Image Unavailable
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-4 self-stretch bg-white p-6">
          <div className="relative">
            <div className="flex flex-col gap-2">
              <h3 className="text-xl font-medium text-brand-700">{title}</h3>
              <div className="m-0 text-base text-brand-700">
                {displayPosition && (
                  <div className="m-0 text-base text-brand-700">
                    {displayPosition}
                  </div>
                )}
              </div>
            </div>

            {shouldShowBio &&
              (category === 'Geschäftsleitung' ||
                category === 'Executive Board' ||
                category === 'Direktion und Kader' ||
                category === 'Executive Management & Management') && (
                <Icon
                  className="absolute right-0 top-0 cursor-pointer"
                  name="square-user-round"
                  color="#07314C"
                  size="20px"
                  onClick={() => setOverlayOpen(true)}
                />
              )}
            <Overlay
              isOpen={isOverlayOpen}
              onClose={() => setOverlayOpen(false)}
              {...overlay}
              name={title}
              image={image}
              position={position}
              positionEn={positionEn}
              email={email}
              linkedIn={linkedIn}
            />
          </div>
          {links && (
            <ul className="card-links flex list-none flex-col gap-2">
              {links
                .filter((link) => link?.title)
                .map((link, index) => (
                  <li key={index}>
                    <Link {...link}>{link.title}</Link>
                  </li>
                ))}
            </ul>
          )}

          {socialShare && socialShare.length > 0 && (
            <ul className="card-links flex list-none flex-row gap-4 border-neutral-50 pt-4">
              {socialShare
                .filter((link) => link?.title)
                .map((link, index) => (
                  <li key={index}>
                    <Link {...link}>{link.title}</Link>
                  </li>
                ))}
            </ul>
          )}

          {phone && (
            <div className="card-links flex list-none flex-row gap-4">
              <Icon name={'phone'} color={'#07314C'} size={'24px'} />
              <a
                href={`tel:${phone}`}
                className="text-brand-700"
                aria-label="phone"
              >
                {phone}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  },
);

TeamCard.displayName = 'TeamCard';

export default TeamCard;
